import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '@core/auth.service';
import { Router } from '@angular/router';
import { Application } from '@enums/application.enums';
import { Location } from '@angular/common';
import { API_URLS } from './api-url.enum';
import { HttpService } from './http.service';
import { CommonService } from '@shared/services/common.service';
import { Fields, StatusCodes } from '@constants/constant';
import { appRoutingUrl } from '@enums/comman.enums';
import { environment } from '@environments/environment';
import { IndexedDbService } from '@services/indexed-db.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  private readonly postTypes: Array<string> = [
    '/image/',
    '/video/',
    '/reel/',
    '/giveaway/',
    '/poll/',
    '/article/'
  ];
  private isRefreshing = false;
  readonly allowedUrls: Array<string> = [API_URLS.pushNotification];
  constructor(
    private readonly router: Router,
    private readonly cs: CommonService,
    private readonly location: Location,
    private readonly authService: AuthService,
    private readonly httpService: HttpService,
    private indexedDbService: IndexedDbService
  ) {}

  intercept(
    request: HttpRequest<string>,
    next: HttpHandler
  ): Observable<HttpEvent<string>> {
    const accessToken = this.authService.accessToken?.value;
    if (accessToken) {
      request = request.clone({
        setHeaders: {
          Authorization: `${accessToken}`
        }
      });
    }
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        switch (err.status) {
          case StatusCodes.BAD_REQUEST:
            if (
              err?.error?.message.toLowerCase() === Fields.NO_ACCESS_TO_CONTENT
            ) {
              this.navigateToProfile(this.location.path());
              return of(null);
            }
            break;
          case Application.FOUR_HUNDRED_ONE:
            return this.handle401Error(request, next);
          case Application.FOUR_HUNDRED_THREE:
            this.authService.removeDetails();
            this.updateProfileTheme(Application.THEME_DARK);
            this.navigateToLogin();
            setTimeout(() => {
              this.cs.updateModuleLoaderStatus(true);
            }, Application.INTERVAL_THREE_THOUSAND);
            break;
        }
        const error = err.error;
        if (err.status === Application.FOUR_HUNDRED_ONE) {
          return throwError(error);
        } else {
          return throwError(error);
        }
      })
    );
  }
  private handle401Error(
    request: HttpRequest<string>,
    next: HttpHandler
  ): Observable<HttpEvent<string>> {
    if (!this.cs.isBrowser) {
      return throwError(() => new Error('Unauthorized'));
    }
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const refreshToken = (this.cs.isBrowser ? JSON.parse(localStorage.getItem(Application.REFRESH_TOKEN)) : null) || this.indexedDbService.getValueFromIndexedDb(Application.REFRESH_TOKEN);
      if (refreshToken) {
        const savedRefreshToken = refreshToken;

        return this.callRefreshToken(
          API_URLS.refreshToken,
          { refreshToken: savedRefreshToken.fm_over },
          false
        ).pipe(
          switchMap((res: any) => {
            this.refreshTokenSubject.next(res.token);
            this.authService.changeToken({ fm_user: res.token });
            setTimeout(() => {
              this.cs.updateModuleLoaderStatus(true);
            }, Application.INTERVAL_THREE_THOUSAND);
            this.isRefreshing = false;
            return next.handle(this.addTokenHeader(request, res.token));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.updateProfileTheme(Application.THEME_DARK);
            this.authService.removeDetails();
            this.navigateToLogin();
            this.cs.showErrorMessage(Application.AUTH_EXPIRED);
            return throwError(() => err);
          })
        );
      } else {
        if (
          !this.allowedUrls.includes(
            request.url.replace(environment.baseURL, '')
          )
        ) {
          this.updateProfileTheme(Application.THEME_DARK);
          this.cs.showErrorMessage(Application.AUTH_EXPIRED);
          this.isRefreshing = false;
          this.authService.removeDetails();
          this.navigateToLogin();
        }
      }
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token!)))
    );
  }

  isSubstringPresentInURL(
    currentBrowserURL: string,
    subString: string
  ): boolean {
    return currentBrowserURL.includes(subString);
  }

  getUserName(
    currentBrowserURL: string,
    separator: string,
    index: number
  ): string {
    return currentBrowserURL.split(separator)?.[index];
  }

  navigateToProfile(currentBrowserURL: string) {
    let username;
    if (
      this.isSubstringPresentInURL(
        currentBrowserURL,
        appRoutingUrl.COURSE_DETAIL
      )
    ) {
      username = this.getUserName(currentBrowserURL, '?username=', 1);
    } else if (
      this.isSubstringPresentInURL(currentBrowserURL, appRoutingUrl.POST_FORUM)
    ) {
      username = this.getUserName(currentBrowserURL, '/', 3);
    } else if (
      this.isSubstringPresentInURL(
        currentBrowserURL,
        appRoutingUrl.TYPE_POST
      ) &&
      this.postTypes.some((substring) =>
        this.isSubstringPresentInURL(currentBrowserURL, substring)
      )
    ) {
      username = this.getUserName(currentBrowserURL, '/', 2);
    }
    if (username) {
      this.router.navigate(['', username]);
    } else return;
  }

  updateProfileTheme(selectedTheme: string): void {
    this.cs.updateTheme(selectedTheme);
  }

  callRefreshToken(endpoint: string, data, loader): Observable<any> {
    return this.httpService.httpRequest(`PUT`, endpoint, loader, data);
  }

  getToken(
    savedRefreshToken: { fm_over: string },
    request: HttpRequest<string>,
    next: HttpHandler
  ) {
    if (savedRefreshToken) {
      return this.callRefreshToken(
        API_URLS.refreshToken,
        { refreshToken: savedRefreshToken?.fm_over },
        false
      ).pipe(
        switchMap((res: any): any => {
          this.refreshTokenSubject.next(res.token);
          const user = { fm_user: res.token };
          this.authService.changeToken(user);
          // window.location.reload();
          setTimeout(() => {
            this.cs.updateModuleLoaderStatus(true);
          }, Application.INTERVAL_THREE_THOUSAND);
          return next.handle(this.addTokenHeader(request, res.token));
        }),
        catchError((err) => {
          this.authService.removeDetails();
          this.navigateToLogin();
          if (err.statusCode !== Application.FOUR_HUNDRED_ONE) {
            return throwError(err);
          } else {
            this.cs.showErrorMessage(Application.AUTH_EXPIRED);
            return throwError('');
          }
        })
      );
    } else {
      this.authService.removeDetails();
      this.navigateToLogin();
    }
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(Application.THREE),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private addTokenHeader(request: HttpRequest<string>, token: string) {
    return request.clone({
      headers: request.headers.set('Authorization', `${String(token)}`)
    });
  }

  navigateToLogin() {
    if (this.cs.isWhiteLabelWeb) {
      const username = this.cs.whiteLabelData.user.user_name;
      this.router.navigate([`/${username}`]);
      return;
    }
    this.router.navigate(['/']);
  }
}
